import { useEffect, useRef, useState, useCallback } from "react";

const COLORS = {
  task: "#FFAB00",
  lesson: "#00E5FF",
  procedure: "#FF2A55",
};

const MIN_SCALE = 0.15;
const MAX_SCALE = 5;

function buildState(nodes, links, w, h) {
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.38;
  return {
    nodes: nodes.map((n, i) => {
      const a = (i / nodes.length) * Math.PI * 2;
      return {
        ...n,
        x: cx + Math.cos(a) * r * (0.5 + 0.5 * Math.random()),
        y: cy + Math.sin(a) * r * (0.5 + 0.5 * Math.random()),
        vx: 0,
        vy: 0,
        size: n.group === "procedure" ? 7 : n.group === "task" ? 5.5 : 4.5,
      };
    }),
    links: links.map((l) => ({
      ...l,
      source: typeof l.source === "string" ? l.source : l.source.id,
      target: typeof l.target === "string" ? l.target : l.target.id,
    })),
  };
}

function getTouchDist(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

export default function MemoryGraph({
  nodes,
  links,
  height = 520,
  highlightId = null,
  compact = false,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: height });
  const tRef = useRef(0);
  const hoverRef = useRef(null);
  const dragRef = useRef(null);   // { node, offsetX, offsetY } — node drag
  const panRef = useRef(null);    // { startX, startY, startTx, startTy } — canvas pan
  const pinchRef = useRef(null);  // { dist, scale, tx, ty } — pinch zoom
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const [hover, setHover] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1); // for UI display only

  const rebuild = useCallback(() => {
    const { w, h } = sizeRef.current;
    if (w <= 0 || h <= 0) return;
    const prev = stateRef.current?.nodes || [];
    const prevById = new Map(prev.map((n) => [n.id, n]));
    const fresh = buildState(nodes, links, w, h);
    fresh.nodes = fresh.nodes.map((n) => {
      const p = prevById.get(n.id);
      if (p) return { ...n, x: p.x, y: p.y, vx: p.vx, vy: p.vy };
      return n;
    });
    stateRef.current = fresh;
  }, [nodes, links]);

  // Resize observer
  useEffect(() => {
    if (!wrapRef.current) return;
    const measure = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      sizeRef.current = { w: rect.width, h: height };
      const c = canvasRef.current;
      if (c) {
        const dpr = window.devicePixelRatio || 1;
        c.width = rect.width * dpr;
        c.height = height * dpr;
        c.style.width = rect.width + "px";
        c.style.height = height + "px";
        const ctx = c.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      rebuild();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [height, rebuild]);

  useEffect(() => { rebuild(); }, [rebuild]);

  // Wheel zoom — attach as non-passive so we can preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const { scale, tx, ty } = transformRef.current;
      const factor = e.deltaY < 0 ? 1.13 : 1 / 1.13;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
      transformRef.current = {
        scale: newScale,
        tx: sx - (sx - tx) * (newScale / scale),
        ty: sy - (sy - ty) * (newScale / scale),
      };
      setZoom(newScale);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, []);

  const getNeighbours = useCallback((id) => {
    if (!id || !stateRef.current) return new Set();
    const set = new Set([id]);
    for (const l of stateRef.current.links) {
      if (l.source === id) set.add(l.target);
      if (l.target === id) set.add(l.source);
    }
    return set;
  }, []);

  // Convert screen → world coords (accounting for pan/zoom)
  const screenToWorld = (sx, sy) => {
    const { scale, tx, ty } = transformRef.current;
    return { x: (sx - tx) / scale, y: (sy - ty) / scale };
  };

  // Physics + render loop
  useEffect(() => {
    let raf;
    const tick = () => {
      tRef.current += 1;
      step();
      draw();
      raf = requestAnimationFrame(tick);
    };

    const step = () => {
      const s = stateRef.current;
      const { w, h } = sizeRef.current;
      if (!s || !w) return;
      const dragId = dragRef.current?.node?.id;
      const idx = new Map(s.nodes.map((n) => [n.id, n]));

      // Repulsion
      for (let i = 0; i < s.nodes.length; i++) {
        const a = s.nodes[i];
        for (let j = i + 1; j < s.nodes.length; j++) {
          const b = s.nodes[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          let dist2 = dx * dx + dy * dy;
          if (dist2 < 1) dist2 = 1;
          const dist = Math.sqrt(dist2);
          const f = 4200 / dist2;
          const fx = (dx / dist) * f;
          const fy = (dy / dist) * f;
          if (a.id !== dragId) { a.vx -= fx; a.vy -= fy; }
          if (b.id !== dragId) { b.vx += fx; b.vy += fy; }
        }
      }

      // Spring forces
      const linkDist = compact ? 90 : 130;
      const k = 0.035;
      for (const l of s.links) {
        const a = idx.get(l.source);
        const b = idx.get(l.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const diff = dist - linkDist;
        const fx = (dx / dist) * diff * k;
        const fy = (dy / dist) * diff * k;
        if (a.id !== dragId) { a.vx += fx; a.vy += fy; }
        if (b.id !== dragId) { b.vx -= fx; b.vy -= fy; }
      }

      // Centering + damping + integrate
      const cx = w / 2;
      const cy = h / 2;
      for (const n of s.nodes) {
        if (n.id === dragId) continue;
        n.vx += (cx - n.x) * 0.0003;
        n.vy += (cy - n.y) * 0.0003;
        n.vx *= 0.88;
        n.vy *= 0.88;
        n.x += n.vx;
        n.y += n.vy;
        // Soft world bounds (2× canvas so nodes don't escape when zoomed out)
        const pad = 24;
        const ww = w * 2, wh = h * 2;
        if (n.x < -ww + pad) n.x = -ww + pad;
        if (n.x > ww - pad) n.x = ww - pad;
        if (n.y < -wh + pad) n.y = -wh + pad;
        if (n.y > wh - pad) n.y = wh - pad;
      }
    };

    const draw = () => {
      const c = canvasRef.current;
      const s = stateRef.current;
      const { w, h } = sizeRef.current;
      if (!c || !s || !w) return;
      const ctx = c.getContext("2d");
      const { scale, tx, ty } = transformRef.current;

      ctx.clearRect(0, 0, w, h);

      // Background (screen space, no transform)
      const wash = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.6);
      wash.addColorStop(0, "rgba(0,229,255,0.05)");
      wash.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = wash;
      ctx.fillRect(0, 0, w, h);

      // Apply zoom/pan transform for graph content
      ctx.save();
      ctx.translate(tx, ty);
      ctx.scale(scale, scale);

      const idx = new Map(s.nodes.map((n) => [n.id, n]));
      const selId = selectedId;
      const neighbours = selId ? getNeighbours(selId) : null;

      // Edges
      ctx.lineCap = "round";
      for (const l of s.links) {
        const a = idx.get(l.source);
        const b = idx.get(l.target);
        if (!a || !b) continue;

        const isHiProp = highlightId && (l.source === highlightId || l.target === highlightId);
        const isHiSel = neighbours && neighbours.has(l.source) && neighbours.has(l.target);

        if (isHiSel) {
          ctx.strokeStyle = "rgba(0,229,255,0.75)";
          ctx.lineWidth = 1.6 / scale;
        } else if (isHiProp) {
          ctx.strokeStyle = "rgba(0,229,255,0.65)";
          ctx.lineWidth = 1.2 / scale;
        } else if (neighbours) {
          ctx.strokeStyle = "rgba(140,200,255,0.06)";
          ctx.lineWidth = 0.5 / scale;
        } else {
          ctx.strokeStyle = "rgba(140,200,255,0.18)";
          ctx.lineWidth = 0.6 / scale;
        }

        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();

        if (isHiSel || isHiProp) {
          const p = ((tRef.current % 90) / 90 + (l.source.length % 7) * 0.13) % 1;
          const px = a.x + (b.x - a.x) * p;
          const py = a.y + (b.y - a.y) * p;
          ctx.fillStyle = "#00E5FF";
          ctx.beginPath();
          ctx.arc(px, py, 1.8 / scale, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Nodes
      const pulse = Math.sin(tRef.current * 0.07);
      for (const n of s.nodes) {
        const color = COLORS[n.group] || "#00E5FF";
        const isHovered = hoverRef.current === n.id;
        const isSelected = selId === n.id;
        const isNeighbour = neighbours && neighbours.has(n.id);
        const dimmed = neighbours && !isNeighbour;
        const r = n.size + (isHovered ? 2.5 : 0) + (isSelected ? 1.5 : 0);

        ctx.globalAlpha = dimmed ? 0.22 : 1.0;

        // Halo
        const halo = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 5);
        halo.addColorStop(0, color + "55");
        halo.addColorStop(0.45, color + "18");
        halo.addColorStop(1, color + "00");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 5, 0, Math.PI * 2);
        ctx.fill();

        if (isSelected || highlightId === n.id) {
          ctx.strokeStyle = color;
          ctx.lineWidth = (isSelected ? 1.8 : 1.2) / scale;
          ctx.beginPath();
          ctx.arc(n.x, n.y, r + 6 + pulse * 3, 0, Math.PI * 2);
          ctx.stroke();
        }

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#030303";
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 0.42, 0, Math.PI * 2);
        ctx.fill();

        if (n.group === "procedure") {
          ctx.strokeStyle = color + "AA";
          ctx.lineWidth = 1 / scale;
          ctx.strokeRect(n.x - r - 3, n.y - r - 3, (r + 3) * 2, (r + 3) * 2);
        }

        ctx.globalAlpha = 1.0;

        // Labels — drawn in world space, font scaled so they stay readable
        const showLabel = isHovered || isSelected || highlightId === n.id ||
          (!compact && n.group === "procedure");
        if (showLabel && !dimmed) {
          const label = n.label || n.id;
          const conf = n.confidence != null ? ` ·${(n.confidence * 100).toFixed(0)}%` : "";
          const text = label + conf;
          const fontSize = Math.max(9, Math.min(13, 11 / scale));
          ctx.font = `${fontSize}px "IBM Plex Mono", monospace`;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          const tw = ctx.measureText(text).width;
          const lx = n.x + r + 8 / scale;
          const ly = n.y;
          const pad2 = 4 / scale;
          const boxH = 16 / scale;
          ctx.fillStyle = "rgba(3,3,3,0.92)";
          ctx.fillRect(lx - pad2, ly - boxH / 2, tw + pad2 * 2, boxH);
          ctx.strokeStyle = color;
          ctx.lineWidth = 0.5 / scale;
          ctx.strokeRect(lx - pad2, ly - boxH / 2, tw + pad2 * 2, boxH);
          ctx.fillStyle = "#F4F4F5";
          ctx.fillText(text, lx, ly);
        }
      }

      ctx.restore();
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [highlightId, compact, selectedId, getNeighbours]);

  // --- Event handlers ---

  const getRawPos = (e) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const hitNode = (sx, sy) => {
    const s = stateRef.current;
    if (!s) return null;
    const { x: wx, y: wy } = screenToWorld(sx, sy);
    const { scale } = transformRef.current;
    for (const n of s.nodes) {
      const dx = n.x - wx;
      const dy = n.y - wy;
      // Hit radius in world space, but at least ~8px on screen
      const hitR = (n.size + 8) / Math.max(scale, 0.5);
      if (dx * dx + dy * dy < hitR * hitR) return n;
    }
    return null;
  };

  const onMouseDown = (e) => {
    const { x, y } = getRawPos(e);
    const n = hitNode(x, y);
    if (n) {
      const { x: wx, y: wy } = screenToWorld(x, y);
      dragRef.current = { node: n, offsetX: wx - n.x, offsetY: wy - n.y };
      n.vx = 0; n.vy = 0;
    } else {
      // Pan
      panRef.current = {
        startX: x, startY: y,
        startTx: transformRef.current.tx,
        startTy: transformRef.current.ty,
      };
    }
  };

  const onMouseMove = (e) => {
    const { x, y } = getRawPos(e);
    if (dragRef.current) {
      const { x: wx, y: wy } = screenToWorld(x, y);
      const n = dragRef.current.node;
      n.x = wx - dragRef.current.offsetX;
      n.y = wy - dragRef.current.offsetY;
      n.vx = 0; n.vy = 0;
      canvasRef.current.style.cursor = "grabbing";
      return;
    }
    if (panRef.current) {
      transformRef.current = {
        ...transformRef.current,
        tx: panRef.current.startTx + (x - panRef.current.startX),
        ty: panRef.current.startTy + (y - panRef.current.startY),
      };
      canvasRef.current.style.cursor = "grabbing";
      return;
    }
    const found = hitNode(x, y);
    hoverRef.current = found?.id || null;
    setHover(found);
    canvasRef.current.style.cursor = found ? "grab" : "default";
  };

  const onMouseUp = () => {
    dragRef.current = null;
    panRef.current = null;
    canvasRef.current.style.cursor = "default";
  };

  const onClick = (e) => {
    if (dragRef.current || panRef.current) return;
    const { x, y } = getRawPos(e);
    const n = hitNode(x, y);
    setSelectedId((prev) => (n && prev !== n.id ? n.id : null));
  };

  const onTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      dragRef.current = null;
      panRef.current = null;
      pinchRef.current = {
        dist: getTouchDist(e.touches),
        ...transformRef.current,
      };
    } else {
      pinchRef.current = null;
      onMouseDown(e);
    }
  };

  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && pinchRef.current) {
      const newDist = getTouchDist(e.touches);
      const ratio = newDist / pinchRef.current.dist;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchRef.current.scale * ratio));
      const rect = canvasRef.current.getBoundingClientRect();
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      transformRef.current = {
        scale: newScale,
        tx: cx - (cx - pinchRef.current.tx) * (newScale / pinchRef.current.scale),
        ty: cy - (cy - pinchRef.current.ty) * (newScale / pinchRef.current.scale),
      };
      setZoom(newScale);
    } else {
      onMouseMove(e);
    }
  };

  const onTouchEnd = (e) => {
    e.preventDefault();
    pinchRef.current = null;
    onMouseUp();
  };

  const applyZoom = (factor) => {
    const { w, h } = sizeRef.current;
    const cx = w / 2;
    const cy = h / 2;
    const { scale, tx, ty } = transformRef.current;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
    transformRef.current = {
      scale: newScale,
      tx: cx - (cx - tx) * (newScale / scale),
      ty: cy - (cy - ty) * (newScale / scale),
    };
    setZoom(newScale);
  };

  const resetView = () => {
    transformRef.current = { scale: 1, tx: 0, ty: 0 };
    setZoom(1);
  };

  return (
    <div ref={wrapRef} data-testid="memory-graph" className="relative w-full select-none" style={{ height }}>
      <canvas
        ref={canvasRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => {
          hoverRef.current = null;
          setHover(null);
          dragRef.current = null;
          panRef.current = null;
        }}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="block"
        style={{ touchAction: "none" }}
      />

      {/* Overlay UI */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top-left label */}
        <div className="absolute top-3 left-3 text-[10px] tracking-[0.3em] text-cyber/70">
          [ NEURAL.GRAPH ]
        </div>

        {/* Top-right: node/edge count + zoom controls */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          <span className="text-[10px] tracking-[0.3em] text-ink-500">
            {nodes.length} NODES · {links.length} EDGES
          </span>
          <div className="flex items-center gap-1 pointer-events-auto">
            <button
              onClick={() => applyZoom(1.3)}
              className="w-6 h-6 border border-line-2 bg-void/80 text-cyber hover:border-cyber flex items-center justify-center text-sm font-bold leading-none"
              title="Zoom in"
            >+</button>
            <button
              onClick={() => applyZoom(1 / 1.3)}
              className="w-6 h-6 border border-line-2 bg-void/80 text-cyber hover:border-cyber flex items-center justify-center text-sm font-bold leading-none"
              title="Zoom out"
            >−</button>
            <button
              onClick={resetView}
              className="px-2 h-6 border border-line-2 bg-void/80 text-ink-500 hover:border-line-1 hover:text-ink-100 text-[9px] tracking-widest uppercase"
              title="Reset view"
            >fit</button>
          </div>
        </div>

        {/* Bottom-left: zoom % + hint */}
        <div className="absolute bottom-3 left-3 flex items-center gap-3 text-[10px] tracking-[0.3em] text-ink-500">
          <span className="text-cyber/60">{Math.round(zoom * 100)}%</span>
          <span>
            {hover
              ? `> ${hover.label || hover.id}`
              : selectedId
              ? `> selected · click to deselect`
              : "> scroll zoom · drag pan · click node"}
          </span>
        </div>

        {/* Bottom-right: legend */}
        <div className="absolute bottom-3 right-3 flex gap-3 text-[10px] tracking-[0.3em]">
          <span className="flex items-center gap-1.5 text-amber">
            <span className="w-1.5 h-1.5 bg-amber" /> TASK
          </span>
          <span className="flex items-center gap-1.5 text-cyber">
            <span className="w-1.5 h-1.5 bg-cyber" /> LESSON
          </span>
          <span className="flex items-center gap-1.5 text-hotpink">
            <span className="w-1.5 h-1.5 bg-hotpink" /> PROC
          </span>
        </div>
      </div>
    </div>
  );
}
